import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	type Breakpoint,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControlLabel,
	IconButton,
	type SxProps,
	type Theme,
} from "@mui/material";
import type { ChangeEvent, FC, ReactNode } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK, LINE_HEIGHT } from "../../../theme/tokens";
import SystemButton from "../SystemButton/SystemButton";
import { portalOverlayProps } from "./portalOverlay";
import { useDialogFocusReturn } from "./useDialogFocusReturn";

export type DialogCheckedState = {
	/**
	 * Stable identity of the checkbox this state belongs to — what callers should match on in
	 * `onConfirm`. Falls back to `label` when the descriptor carries no `id`, so callers written
	 * before ids existed keep resolving.
	 */
	id?: string;
	label: string;
	required: boolean;
	checked: boolean;
};

export type DialogCheckboxDescriptor = {
	/**
	 * Stable, never-translated identity. Without it the label doubles as the key, which breaks the
	 * moment the label is translated or interpolated with a name/count.
	 */
	id?: string;
	label: string;
	/**
	 * Secondary line under the label, for the consequence the label itself shouldn't have to spell
	 * out. Wired to the checkbox via `aria-describedby`, so AT reads it as part of the control.
	 */
	helperText?: ReactNode;
	required: boolean;
	defaultValue: boolean;
};

/**
 * Lines the helper text up under the label rather than under the checkbox: 31px = the 42px checkbox
 * control minus `FormControlLabel`'s own -11px left margin.
 */
const CHECKBOX_HELPER_INDENT = 3.875;

// DEFAULT: Legacy descriptors predate `id`; the uniqueness check below makes their label identity safe.
const checkboxIdentity = (checkbox: DialogCheckboxDescriptor): string => checkbox.id ?? checkbox.label;

/** Duplicate identities alias state and required gating, so fail at the descriptor boundary. */
const assertUniqueCheckboxIdentities = (checkboxes: DialogCheckboxDescriptor[]): void => {
	const identities = checkboxes.map(checkboxIdentity);
	const duplicate = identities.find((identity, index) => identities.indexOf(identity) !== index);
	if (duplicate !== undefined) throw new Error(`SystemDialog checkbox identity must be unique: ${duplicate}`);
};

/** The descriptors' declared starting state — used both on first render and on every reset. */
const buildCheckedStates = (checkboxes: DialogCheckboxDescriptor[]): DialogCheckedState[] =>
	checkboxes.map((checkbox) => ({
		// DEFAULT: `label` is the legacy identity — callers that predate `id` match on it, so falling
		// back keeps them resolving instead of silently reporting an id-less state.
		id: checkboxIdentity(checkbox),
		label: checkbox.label,
		required: checkbox.required,
		checked: checkbox.defaultValue,
	}));

/**
 * Drops the label's first line to the checkbox's centre line — half the difference between the
 * 42px checkbox control and a 24px line of body text.
 */
const CHECKBOX_LABEL_FIRST_LINE_OFFSET = 1.125;

/**
 * An optional secondary action rendered alongside the primary confirm button.
 * Use this when the dialog offers two real choices (e.g. "apply the change" vs
 * "keep the current state") rather than a single confirm + a passive cancel.
 * Distinct from `cancel`, which always just closes the dialog.
 */
export type DialogSecondaryAction = {
	label: ReactNode;
	onClick: (checkedState?: DialogCheckedState[]) => void;
	loading?: boolean;
	disabled?: boolean;
	tooltip?: string;
	buttonVariant?: "primary" | "secondary" | "borderless" | "delete";
};

type Props = {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	body: ReactNode;
	cancel?: ReactNode;
	confirm?: ReactNode;
	onConfirm: (checkedState?: DialogCheckedState[]) => void;
	/** Optional second action (besides confirm) — renders an extra button. */
	secondaryAction?: DialogSecondaryAction;
	confirmTooltip?: string;
	confirmDisabled?: boolean;
	/** Variant for the primary confirm button (defaults to the borderless text style). */
	confirmButtonVariant?: "primary" | "secondary" | "borderless" | "delete";
	/**
	 * Opt-in: the confirm button spans the dialog's full width and the action row stacks. For a dialog
	 * whose confirm IS the whole decision (a picker whose footer commits the value chosen above), where
	 * a right-aligned button reads as an afterthought. Off by default, so no existing dialog moves.
	 */
	fullWidthConfirm?: boolean;
	/**
	 * Layout of the action row. For a dialog whose two buttons are a real pair — a bordered Cancel beside
	 * a wide primary action — rather than the default "passive cancel, then confirm, right-aligned".
	 * Merged after the built-in layout, so a caller can override the padding and the gap without also
	 * having to opt into `fullWidthConfirm`'s stacked column.
	 */
	actionsSx?: SxProps<Theme>;
	/** Cancel's weight. `borderless` (the default) reads as passive; `secondary` gives it a real edge. */
	cancelButtonVariant?: "borderless" | "secondary";
	/** Leading glyph inside the confirm button (e.g. a check on a commit action). */
	confirmLeadingIcon?: ReactNode;
	/** Extra styling for the confirm button — e.g. a design-specified fill that isn't a theme variant. */
	confirmSx?: SxProps<Theme>;
	checkboxes?: DialogCheckboxDescriptor[];
	zIndex?: number;
	/** Optional max-width breakpoint for the underlying MUI Dialog (defaults to MUI's "sm"). */
	maxWidth?: Breakpoint | false;
	/** Stretch the dialog to its max-width breakpoint. */
	fullWidth?: boolean;
	/** Cover the viewport (a rich dialog on a phone). */
	// DEFAULT: absent = MUI's floating dialog.
	fullScreen?: boolean;
	/** Styling for the body (`DialogContent`). */
	// DEFAULT: absent = MUI's body padding.
	contentSx?: SxProps<Theme>;
	/** Tools at the start of the action row that act on the content rather than closing it (Reset, "Surprise me"). */
	// DEFAULT: absent = no start group; cancel/confirm keep their place.
	actionsStart?: ReactNode;
	hideCancel?: boolean;
	/**
	 * Opt-in ✕ in the title row, for a dialog big enough that its Cancel button is a long way from where
	 * the eye is — a composer, a multi-section form. It calls the same `onClose` as Cancel and the
	 * backdrop, so it adds a reach, never a second way out. Off by default: a short confirm dialog whose
	 * two buttons are already in view does not need one.
	 */
	showCloseIcon?: boolean;
	/**
	 * Drop the confirm button entirely — for a dialog whose BODY is the answer (picking a row in a
	 * a single-select list, say, where the selection IS the commit). Leaves Cancel as the only footer
	 * action, so the modal still has an explicit, non-destructive way out.
	 */
	hideConfirm?: boolean;
	cancelLoading?: boolean;
	confirmLoading?: boolean;
	/**
	 * Make a NODE body the dialog's accessible description too (a string body always is): it is wrapped in
	 * an element the dialog's `aria-describedby` points at, so a screen reader reads it on open. For a body
	 * that IS the explanation — several lines, a count looked up on open. Off by default, so existing
	 * callers' DOM and ARIA are unchanged.
	 */
	describeBody?: boolean;
	/**
	 * Announce the dialog as an `alertdialog` — for a confirmation whose consequence the user must take
	 * in before acting (a destructive remove). A string `body` is already wired as `aria-describedby`, so
	 * screen readers read it with the title on open; a node body should carry its own description. Off by
	 * default: every existing dialog keeps `role="dialog"`.
	 */
	alert?: boolean;
	/**
	 * Where focus lands on open. `"cancel"` puts it on Cancel, so a destructive dialog's Enter/Space
	 * backs out rather than confirming. Absent = MUI's default (the dialog surface). Inert with
	 * `hideCancel`, since there is no Cancel to focus.
	 */
	initialFocus?: "cancel";
	/**
	 * The id(s), space-separated, of the element(s) that describe a NODE body, wired as the dialog's `aria-describedby` — so an
	 * `alert` dialog whose body is markup (a paragraph plus notices) is still read with its description on
	 * open. A string body is wired automatically and ignores this. Absent = no description for a node body.
	 */
	describedById?: string;
	dataTestId?: string;
	/**
	 * Exact test ids for the Cancel / Confirm buttons, overriding the derived `{dataTestId}-cancel-button` /
	 * `{dataTestId}-confirm-button` — for a dialog migrated onto SystemDialog whose long-standing ids
	 * (e.g. `{name}-dialog-confirm`) e2e suites already rely on. Unset = the derived ids, unchanged.
	 */
	cancelDataTestId?: string;
	confirmDataTestId?: string;
	secondaryDataTestId?: string;
};

/**
 * SystemDialog component is a reusable dialog component for displaying system messages.
 * @param onConfirm - Callback function to execute when the confirm button is clicked.
 * @param body - The content of the dialog body, typically a message or information.
 * @param onClose - Callback function to execute when the dialog is closed, typically for the cancel button.
 * @param title - The title of the dialog, displayed at the top.
 * @param open - Boolean to control the visibility of the dialog.
 * @param cancel - Text for the cancel button, defaults to "Cancel".
 * @param confirm - Text for the confirm button, defaults to "Confirm".
 * @param confirmDisabled - Boolean to disable the confirm button.
 * @param checkboxes - Checkboxes to display in the dialog. Each carries an optional stable `id`
 * (what `onConfirm` should match on — the label is translated), an optional `helperText` line, and
 * `required`, which keeps confirm disabled until it is ticked.
 * @param zIndex - zIndex for the dialog.
 * @param hideCancel
 * @param confirmLoading - Boolean to show loading state on the confirm button.
 * @param cancelLoading - Boolean to show loading state on the cancel button.
 * @param confirmTooltip - Tooltip text for the confirm button.
 */
const SystemDialog: FC<Props> = ({
	onConfirm,
	body,
	onClose,
	title,
	open,
	cancel,
	confirm,
	secondaryAction,
	confirmDisabled,
	confirmButtonVariant = "borderless",
	fullWidthConfirm,
	actionsSx,
	cancelButtonVariant = "borderless",
	confirmLeadingIcon,
	confirmSx,
	checkboxes = [],
	zIndex,
	maxWidth,
	fullWidth,
	fullScreen,
	contentSx,
	actionsStart,
	hideCancel = false,
	hideConfirm = false,
	showCloseIcon = false,
	confirmLoading,
	cancelLoading,
	confirmTooltip,
	// DEFAULT: an optional, additive prop — off keeps every existing caller's DOM and ARIA as they were.
	describeBody = false,
	alert,
	initialFocus,
	describedById,
	dataTestId,
	cancelDataTestId,
	confirmDataTestId,
	secondaryDataTestId,
}) => {
	const { t } = useTranslation();
	const checkboxIdPrefix = useId();
	const titleId = useId();
	const descriptionId = useId();
	const [checkedStates, setCheckedStates] = useState<DialogCheckedState[]>([]);
	const dialogRef = useRef<HTMLDivElement>(null);
	// Focus goes back to where it came from after the exit transition, not as `open` turns false (`useDialogFocusReturn`).
	const focusReturn = useDialogFocusReturn(open, dialogRef);
	const cancelText = cancel ?? t("cancel");
	const confirmText = confirm ?? t("confirm");
	// DEFAULT: existing callers keep the derived secondary-action test id unless they explicitly override it.
	const secondaryButtonTestId = secondaryDataTestId ?? (dataTestId ? `${dataTestId}-secondary-button` : undefined);
	assertUniqueCheckboxIdentities(checkboxes);

	/**
	 * What the reset keys on — deliberately the checkboxes' shape, NOT the array's object identity.
	 * A caller whose descriptor array is rebuilt on every render (`useNurseFeatureData`'s deps
	 * include context functions that are re-created by their provider) would otherwise have the
	 * user's ticks wiped mid-dialog by an unrelated re-render, silently re-arming a required
	 * acknowledgement gate the user had already satisfied.
	 */
	const checkboxSignature = JSON.stringify(
		checkboxes.map((checkbox) => [checkbox.id ?? checkbox.label, checkbox.label, checkbox.required, checkbox.defaultValue]),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: `checkboxSignature` intentionally stands in for `checkboxes` (see above).
	useEffect(() => {
		setCheckedStates(buildCheckedStates(checkboxes));
	}, [checkboxSignature, open]);

	const handleClose = () => {
		// Reset to the descriptors, not to blanks. Blanks dropped `required`, so a dialog closed and
		// reopened without remounting came back with its acknowledgement gate already satisfied.
		setCheckedStates(buildCheckedStates(checkboxes));
		onClose();
	};

	const handleCheckboxChange = (id: string) => (event: ChangeEvent<HTMLInputElement>) => {
		const { checked } = event.target;
		// Keyed by id and replaced rather than mutated: the previous version wrote through a shallow
		// array copy into the shared state objects, so it only rendered because the array identity
		// changed — and it re-broke as soon as anything memoized on the state objects themselves.
		setCheckedStates((prev) => prev.map((state) => (state.id === id ? { ...state, checked } : state)));
	};

	const allCheckboxesChecked =
		checkboxes.length > 0
			? checkedStates.length === checkboxes.length && checkedStates.every((current) => !current.required || (current.required && current.checked))
			: true;

	const requiredCheckboxesUnmet = checkboxes.length > 0 && !allCheckboxesChecked;
	const isConfirmDisabled = confirmDisabled || requiredCheckboxesUnmet;

	/**
	 * Focus Cancel as it mounts. A callback ref rather than an effect: the dialog portals, so its content
	 * mounts a render after `open` flips and an effect would find no button yet. Focusing during the
	 * commit also puts focus inside the modal before MUI's focus trap runs, so the trap keeps it there;
	 * focus still returns to the opener on close (`useDialogFocusReturn`). Memoized so it fires once per
	 * mount, not per render.
	 */
	const focusCancelOnMount = useCallback(
		(button: HTMLButtonElement | null) => {
			if (button && initialFocus === "cancel") button.focus();
		},
		[initialFocus],
	);

	/**
	 * The dialog explains its OWN gate. A greyed-out confirm with nothing to hover is a dead end —
	 * especially on the destructive variant, whose disabled treatment still reads as a live button.
	 * A caller-supplied `confirmTooltip` wins, since it describes the caller's own reason.
	 */
	const effectiveConfirmTooltip = confirmTooltip ?? (requiredCheckboxesUnmet ? t("system.dialog.confirm.requires_acknowledgement") : undefined);
	// A confirm with nothing beside it reads as stranded in a phone-width dialog's corner, so there it is centred;
	// wider dialogs keep MUI's trailing edge, where a lone confirm sits under the reading line's end.
	const isLoneConfirm = hideCancel && !hideConfirm && secondaryAction === undefined && actionsStart === undefined;

	return (
		<Dialog
			ref={dialogRef}
			data-testid={dataTestId}
			open={open}
			onClose={handleClose}
			disableRestoreFocus
			onFocus={focusReturn.onFocus}
			slotProps={{ transition: { onExited: focusReturn.onExited } }}
			maxWidth={maxWidth}
			fullWidth={fullWidth}
			fullScreen={fullScreen}
			aria-labelledby={titleId}
			aria-describedby={typeof body === "string" || describeBody ? descriptionId : describedById}
			// DEFAULT: absent `alert` leaves MUI's own `role="dialog"`.
			role={alert ? "alertdialog" : undefined}
			{...portalOverlayProps("system-dialog")}
			sx={{
				zIndex: zIndex,
				"& .MuiDialogTitle-root": { pb: 0.5 },
				"& .MuiDialogTitle-root + .MuiDialogContent-root": { pt: 1.5 },
			}}
		>
			<DialogTitle color={INK.SECONDARY} id={titleId} sx={showCloseIcon ? { display: "flex", alignItems: "flex-start", gap: 2 } : undefined}>
				{showCloseIcon ? <Box sx={{ flex: 1, minWidth: 0 }}>{title}</Box> : title}
				{showCloseIcon && (
					<IconButton
						onClick={handleClose}
						aria-label={t("close")}
						size="small"
						data-testid={dataTestId ? `${dataTestId}-close-icon` : undefined}
						sx={{ color: INK.MUTED, mt: -0.5, mr: -1 }}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				)}
			</DialogTitle>
			<DialogContent sx={contentSx}>
				{typeof body === "string" ? (
					<DialogContentText id={descriptionId}>{body}</DialogContentText>
				) : describeBody ? (
					<Box id={descriptionId}>{body}</Box>
				) : (
					body
				)}

				{checkboxes.length > 0 && (
					// `align-items: flex-start` keeps each row hugging its own content, so a lone checkbox's
					// clickable area stays the size it was before helper text made these stack.
					<Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
						{checkboxes.map((current, index) => {
							const helperId = current.helperText ? `${checkboxIdPrefix}-checkbox-helper-${index}` : undefined;
							// Resolved by id, not by position, so state can never be read off the wrong row if a
							// caller renders a conditional checkbox (the single-nurse dialog adds two only when
							// the employee has future shifts).
							const checkboxId = checkboxIdentity(current);
							return (
								<Box key={checkboxId}>
									<FormControlLabel
										control={
											<Checkbox
												checked={checkedStates.find((state) => state.id === checkboxId)?.checked || false}
												onChange={handleCheckboxChange(checkboxId)}
												name={`checkbox-${index}`}
												slotProps={helperId ? { input: { "aria-describedby": helperId } } : undefined}
											/>
										}
										label={current.label}
										// A wrapping acknowledgement ("I understand …") vertically-centred its whole
										// paragraph against the box, leaving the checkbox floating beside line two.
										// Anchor it to the first line instead; the offset equals what centring already
										// produced for a one-line label, so short labels are pixel-identical.
										sx={{ alignItems: "flex-start", "& .MuiFormControlLabel-label": { mt: CHECKBOX_LABEL_FIRST_LINE_OFFSET } }}
									/>
									{current.helperText && (
										<Box
											id={helperId}
											sx={{ pl: CHECKBOX_HELPER_INDENT, mt: -0.5, color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, lineHeight: LINE_HEIGHT.DENSE_PROSE }}
										>
											{current.helperText}
										</Box>
									)}
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions
				sx={[
					...(fullWidthConfirm ? [{ flexDirection: "column" as const, alignItems: "stretch" as const, gap: 1, px: 3, pb: 3 }] : []),
					...(isLoneConfirm ? [{ justifyContent: { xs: "center", sm: "flex-end" } }] : []),
					...(Array.isArray(actionsSx) ? actionsSx : [actionsSx]),
				]}
			>
				{actionsStart && <Box sx={{ display: "flex", alignItems: "center", gap: 1, marginInlineEnd: "auto" }}>{actionsStart}</Box>}
				{!hideCancel && (
					<SystemButton
						ref={initialFocus ? focusCancelOnMount : undefined}
						onClick={handleClose}
						buttonVariant={cancelButtonVariant}
						sx={{
							// A bordered Cancel carries its own ink; only the passive one borrows the brand colour.
							...(cancelButtonVariant === "borderless" ? { color: "primary.main" } : {}),
							fontSize: "14px",
						}}
						loading={cancelLoading}
						data-testid={cancelDataTestId ?? (dataTestId ? `${dataTestId}-cancel-button` : undefined)}
					>
						{cancelText}
					</SystemButton>
				)}
				{secondaryAction && (
					<SystemButton
						tooltip={secondaryAction.tooltip}
						loading={secondaryAction.loading}
						disabled={secondaryAction.disabled}
						onClick={() => secondaryAction.onClick(checkboxes.length > 0 ? checkedStates : undefined)}
						buttonVariant={secondaryAction.buttonVariant ?? "secondary"}
						sx={{ fontSize: "14px" }}
						data-testid={secondaryButtonTestId}
					>
						{secondaryAction.label}
					</SystemButton>
				)}
				{!hideConfirm && (
					<SystemButton
						tooltip={effectiveConfirmTooltip}
						loading={confirmLoading}
						disabled={isConfirmDisabled}
						onClick={(_) => onConfirm(checkboxes.length > 0 ? checkedStates : undefined)}
						buttonVariant={confirmButtonVariant}
						leadingIcon={confirmLeadingIcon}
						sx={[
							{
								...(confirmButtonVariant === "borderless" ? { color: "primary.main" } : {}),
								fontSize: "14px",
							},
							// Responsive form on purpose: SystemButton's base sets `width: { xs, sm }`, which compiles to a
							// media block that outranks a plain `width: 100%` — measured 154px in a 600px row before this.
							...(fullWidthConfirm ? [{ width: { xs: "100%", sm: "100%" }, maxWidth: "none" }] : []),
							...(Array.isArray(confirmSx) ? confirmSx : [confirmSx]),
						]}
						data-testid={confirmDataTestId ?? (dataTestId ? `${dataTestId}-confirm-button` : undefined)}
					>
						{confirmText}
					</SystemButton>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default SystemDialog;
